class PredictiveSearch extends HTMLElement {
    constructor() {
        super();
        this.config = {
            modal: true,          // Toggle modal behavior (adds/removes body overflow)
            debounceDelay: 300,   // Input debounce delay in ms,
            fallbackImage: theme?.fallbackImage || null, // Fallback image for products/collections
        };

        this.setupElements();
        this.setupEventListeners();
    }

    setupElements() {
        // Cache frequently used DOM elements
        this.inputElements = Array.from(this.querySelectorAll('input[data-predictive-search]'));
        this.closeSearchButton = this.querySelector('.close--search');
        this.searchModal = this.querySelector('#predictive-search');
        this.spinner = this.querySelector('.search--spinner');
        this.collectionResults = this.querySelector('.collectionSuggestions--results');
        this.productResults = this.querySelector('.productSuggestions--results');
        this.pageResults = this.querySelector('.pageSuggestions--results');
        this.collectionSuggestions = this.querySelector('#collectionSuggestions');
        this.productSuggestions = this.querySelector('#productSuggestions');
        this.pagesSuggestions = this.querySelector('#pagesSuggestions');
        this.viewAllResults = this.querySelector('#viewAllResults');
        this.noResults = this.querySelector('#noResults');
        this.productTemplateSource = $("#product-item").html();
        this.collectionTemplateSource = $("#collection-item").html();
        this.pageTemplateSource = $("#page-item").html();
        this.headerElement = document.querySelector('[data-header]');

        // Cache tab buttons
        this.tabButtons = Array.from(this.querySelectorAll('.search-tabs .tab'));
    }

    setupEventListeners() {
        // Attach debounced input listener to each search input
        this.inputElements.forEach(input => {
            input.addEventListener('input', this.debounce((event) => this.onInputChange(event), this.config.debounceDelay));
        });

        // Attach click listener to close button
        if (this.closeSearchButton) {
            this.closeSearchButton.addEventListener('click', () => this.close());
        }

        // Close modal on pressing ESC
        document.addEventListener('keydown', (event) => {
            if (event.key === 'Escape' || event.key === 'Esc') {
                this.close();
            }
        });

        // Initialize tab event listeners
        this.setupTabs();
    }

    setupTabs() {
        // When a tab is clicked, activate its corresponding suggestion container
        this.tabButtons.forEach(tab => {
            tab.addEventListener('click', (event) => {
                event.preventDefault();
                this.activateTab(tab);
            });
        });
    }

    activateTab(selectedTab) {
        // Remove the active state from all tabs and add it to the selected tab
        this.tabButtons.forEach(tab => tab.classList.remove('is--active'));
        selectedTab.classList.add('is--active');

        // Hide all suggestion containers
        [this.productSuggestions, this.collectionSuggestions, this.pagesSuggestions].forEach(container => {
            container.classList.add('hidden');
        });

        // Show the suggestion container for the selected tab
        const targetId = selectedTab.getAttribute('data-tab-target');
        const targetContainer = this.querySelector(`#${targetId}`);
        if (targetContainer) {
            targetContainer.classList.remove('hidden');
        }
    }

    onInputChange(event) {
        const searchTerm = event.target.value.trim();
        if (!searchTerm.length) {
            this.close();
            return;
        }
        this.fetchSearchResults(searchTerm);
    }

    fetchSearchResults(searchTerm) {
        this.showSpinner();

        // Encode search term to avoid special character issues
        const url = `/search/suggest.json?q=${encodeURIComponent(searchTerm)}&resources[type]=product,collection,page&resources[limit]=10&resources[options][unavailable_products]=hide&resources[options][fields]=title,product_type,variants.title`;
        fetch(url)
            .then(response => response.json())
            .then((suggestions) => {
                this.handleSearchResults(suggestions.resources);
            })
            .catch(error => {
                console.error("Error fetching search results:", error);
                this.hideSpinner();
            });
    }

    handleSearchResults(resources) {
        // Clear previous results
        this.clearResults();

        // Open modal if configured
        if (this.config.modal) {
            this.open();
        }

        const collections = resources.results.collections || [];
        const products = resources.results.products || [];
        const pages = resources.results.pages || [];

        // Render Collections
        if (collections.length > 0) {
            this.collectionSuggestions.classList.remove('hidden');
            collections.forEach(item => {
                this.collectionResults.insertAdjacentHTML('beforeend', this.collectionTemplate(item));
            });
        } else {
            this.collectionSuggestions.classList.add('hidden');
        }

        // Render Products
        if (products.length > 0) {
            this.productSuggestions.classList.remove('hidden');
            this.viewAllResults.classList.remove('hidden');
            products.forEach(item => {
                this.productResults.insertAdjacentHTML('beforeend', this.productTemplate(item));
            });
        } else {
            this.productSuggestions.classList.add('hidden');
            this.viewAllResults.classList.add('hidden');
        }

        // Render Pages
        if (pages.length > 0) {
            this.pagesSuggestions.classList.remove('hidden');
            pages.forEach(item => {
                this.pageResults.insertAdjacentHTML('beforeend', this.pageTemplate(item));
            });
        } else {
            this.pagesSuggestions.classList.add('hidden');
        }

        // Toggle no-results message if needed
        if (collections.length === 0 && products.length === 0 && pages.length === 0) {
            this.noResults.classList.remove('hidden');
        } else {
            this.noResults.classList.add('hidden');
        }

        // Update result count in each tab's span
        this.updateTabCounts({
            productSuggestions: products.length,
            collectionSuggestions: collections.length,
            pagesSuggestions: pages.length,
        });

        // Activate default tab based on available results
        // Priority: Products > Collections > Pages
        let defaultTab;
        if (products.length > 0) {
            defaultTab = this.tabButtons.find(tab => tab.getAttribute('data-tab-target') === 'productSuggestions');
        } else if (collections.length > 0) {
            defaultTab = this.tabButtons.find(tab => tab.getAttribute('data-tab-target') === 'collectionSuggestions');
        } else if (pages.length > 0) {
            defaultTab = this.tabButtons.find(tab => tab.getAttribute('data-tab-target') === 'pagesSuggestions');
        } else {
            // Fallback to products if no results found
            defaultTab = this.tabButtons.find(tab => tab.getAttribute('data-tab-target') === 'productSuggestions');
        }
        this.activateTab(defaultTab);

        this.hideSpinner();
    }

    updateTabCounts(counts) {
        this.tabButtons.forEach(tab => {
            const target = tab.getAttribute('data-tab-target');
            const count = counts[target] || 0;
            const span = tab.querySelector('[data-results-total]');
            if (span) {
                span.textContent = count;
            }
            // Hide the tab if count is 0; otherwise, display it
            tab.style.display = count === 0 ? 'none' : '';
        });
    }

    clearResults() {
        if (this.collectionResults) this.collectionResults.innerHTML = '';
        if (this.productResults) this.productResults.innerHTML = '';
        if (this.pageResults) this.pageResults.innerHTML = '';
    }

    showSpinner() {
        if (this.spinner) {
            this.spinner.classList.remove('hidden');
        }
    }

    hideSpinner() {
        if (this.spinner) {
            this.spinner.classList.add('hidden');
        }
    }

    open() {
        if (!this.searchModal) return;
        this.searchModal.classList.remove('hidden');

        // Adjust modal height based on header height if available
        if (this.headerElement) {
            const headerHeight = this.headerElement.offsetHeight;
            this.searchModal.style.height = `${window.innerHeight - headerHeight}px`;
        } else {
            this.searchModal.style.height = `${window.innerHeight}px`;
        }

        // Adjust for an announcement bar if present
        const announcementBar = document.querySelector('[data-announcement-bar-wrapper]');
        if (announcementBar) {
            this.searchModal.style.top = 'var(--header-height)';
        }

        // Prevent background scrolling when modal is open
        if (this.config.modal) {
            document.body.classList.add('overflow-hidden');
        }
    }

    close() {
        if (this.searchModal) {
            this.searchModal.classList.add('hidden');
        }
        if (this.config.modal) {
            document.body.classList.remove('overflow-hidden');
        }
    }

    pageTemplate(data) {
        if (!this.pageTemplateSource) {
            console.error("Page template source is not defined.");
            return '';
        }
        const template = Handlebars.compile(this.pageTemplateSource);
        return template({
            page: data,
            fallback_image: this.config.fallbackImage || null
        });
    }

    collectionTemplate(data) {

        if (!this.collectionTemplateSource) {
            console.error("Collection template source is not defined.");
            return '';
        }
        const template = Handlebars.compile(this.collectionTemplateSource);
        console.log("Collection template data:", data);
        return template({
            collection: data,
            fallback_image: this.config.fallbackImage || null
        });
    }

    productTemplate(data) {
        if (!this.productTemplateSource) {
            console.error("Product template source is not defined.");
            return '';
        }
        const template = Handlebars.compile(this.productTemplateSource);
        return template({
            product: data,
            fallback_image: this.config.fallbackImage || null
        });
    }

    debounce(fn, wait) {
        let timeout;
        return (...args) => {
            clearTimeout(timeout);
            timeout = setTimeout(() => fn.apply(this, args), wait);
        };
    }
}

customElements.define('predictive-search', PredictiveSearch);
